import { findMerchantMatch } from "../categorization/merchantKnowledge";
import { mapPlaidCategory } from "../categorization/plaidCategoryMapping";
import { API_URL, getAuthHeaders, handleApiError } from "../api/api";

export interface AICategorization {
  transaction_index: number;
  general_category: string | null;
  federal_category: string | null;
  oregon_category: string | null;
  confidence: "high" | "medium" | "low" | "uncertain" | "user_verified";
  confidence_score?: number;
  reasoning: string;
  needs_user_input?: boolean;
  source?: "merchant" | "plaid" | "pattern" | "ai" | "user";
}

export interface AICategorizeResponse {
  success: boolean;
  categorizations: AICategorization[];
  processed: number;
  needsUserInput: number;
}

export interface LearnedPattern {
  merchant_pattern?: string;
  transaction_pattern?: string;
  general_category: string;
  federal_category?: string;
  oregon_category?: string;
}

export const aiCategorizationService = {
  // Categorize transactions using AI with learned patterns
  categorizeTransactions: async (
    transactions: any[],
    learnedPatterns: LearnedPattern[] = []
  ): Promise<AICategorizeResponse> => {
    try {
      // For now, use mock data to test the UI while API is not available
      console.log(
        "AI Categorization requested for",
        transactions.length,
        "transactions"
      );

      // Check if we have a backend API available
      const fullUrl = `${API_URL}/ai/categorize-transactions`;

      console.log("Attempting to call AI API at:", fullUrl);

      const headers = await getAuthHeaders();

      const response = await fetch(fullUrl, {
        method: "POST",
        headers,
        body: JSON.stringify({ transactions, learnedPatterns }),
      });

      if (!response.ok) {
        console.error(
          "AI API response not ok:",
          response.status,
          response.statusText
        );
        await handleApiError(response);
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error: any) {
      console.error("AI Categorization error:", error);

      // Return mock categorization data for testing
      console.log("Using mock categorization for demo purposes");

      // Enhanced categorizations using merchant knowledge and Plaid mappings
      const mockCategorizations: AICategorization[] = transactions.map(
        (t, index) => {
          let generalCategory = null;
          let federalCategory = null;
          let oregonCategory = null;
          let confidence: AICategorization["confidence"] = "low";
          let confidenceScore = 0.3;
          let reasoning = "";
          let source: AICategorization["source"] = "ai";
          let needsInput = true;

          // 1. First, check merchant knowledge base
          const merchantMatch = findMerchantMatch(t.merchant_name, t.name);
          if (merchantMatch) {
            generalCategory = merchantMatch.general_category;
            federalCategory = merchantMatch.federal_category;
            oregonCategory = merchantMatch.oregon_category;
            confidenceScore = merchantMatch.confidence;
            reasoning = `Matched known merchant: ${
              merchantMatch.merchant_name || t.merchant_name
            }`;
            source = "merchant";

            if (confidenceScore >= 0.9) {
              confidence = "high";
              needsInput = false;
            } else if (confidenceScore >= 0.7) {
              confidence = "medium";
              needsInput = false;
            } else if (confidenceScore >= 0.5) {
              confidence = "low";
              needsInput = true;
            }
          }

          // 2. If no merchant match or low confidence, try Plaid category mapping
          if (!merchantMatch || confidenceScore < 0.7) {
            const plaidMapping = mapPlaidCategory(t.category);
            if (plaidMapping) {
              // Only override if Plaid has higher confidence
              if (!merchantMatch || plaidMapping.confidence > confidenceScore) {
                generalCategory = plaidMapping.general_category;
                federalCategory =
                  plaidMapping.federal_category || federalCategory;
                oregonCategory = plaidMapping.oregon_category || oregonCategory;
                confidenceScore = plaidMapping.confidence;
                reasoning = `Categorized using Plaid category: ${
                  Array.isArray(t.category)
                    ? t.category.join(" > ")
                    : t.category
                }`;
                source = "plaid";

                if (plaidMapping.requires_context) {
                  needsInput = true;
                  reasoning += ` (${
                    plaidMapping.context_hint || "Requires context"
                  })`;
                } else {
                  needsInput = confidenceScore < 0.7;
                }

                if (confidenceScore >= 0.9) {
                  confidence = "high";
                } else if (confidenceScore >= 0.7) {
                  confidence = "medium";
                } else if (confidenceScore >= 0.5) {
                  confidence = "low";
                }
              }
            }
          }

          // 3. Check learned patterns
          if (learnedPatterns.length > 0) {
            const pattern = learnedPatterns.find((p) => {
              if (p.merchant_pattern) {
                const regex = new RegExp(p.merchant_pattern, "i");
                return regex.test(t.merchant_name || t.name);
              }
              return false;
            });

            if (pattern) {
              generalCategory = pattern.general_category;
              federalCategory = pattern.federal_category || federalCategory;
              oregonCategory = pattern.oregon_category || oregonCategory;
              confidenceScore = 0.95; // High confidence for user patterns
              confidence = "high";
              reasoning = `Applied learned pattern from previous categorizations`;
              source = "pattern";
              needsInput = false;
            }
          }

          // 4. Fallback to basic categorization if still no match
          if (!generalCategory) {
            const merchantLower = (t.name || "").toLowerCase();

            // Basic fallback patterns
            if (
              merchantLower.includes("rent") ||
              merchantLower.includes("property")
            ) {
              generalCategory = "Rent";
              federalCategory = "rent";
              confidence = "medium";
              confidenceScore = 0.7;
              needsInput = false;
            } else if (
              merchantLower.includes("grocery") ||
              merchantLower.includes("market")
            ) {
              generalCategory = "Groceries";
              confidence = "medium";
              confidenceScore = 0.7;
              needsInput = false;
            } else {
              generalCategory = "Other";
              confidence = "uncertain";
              confidenceScore = 0.2;
              needsInput = true;
            }

            if (!reasoning) {
              reasoning = `Basic pattern matching on: ${t.name}`;
            }
          }

          return {
            transaction_index: index + 1,
            general_category: generalCategory,
            federal_category: federalCategory || null,
            oregon_category: oregonCategory || null,
            confidence,
            confidence_score: confidenceScore,
            reasoning,
            needs_user_input: needsInput,
            source,
          };
        }
      );

      return {
        success: true,
        categorizations: mockCategorizations,
        processed: mockCategorizations.length,
        needsUserInput: mockCategorizations.filter((c) => c.needs_user_input)
          .length,
      };
    }
  },
};
